import type { ReactNode } from 'react';
import { Icon } from '../../components/Icon';

export const STEPS = [
  'Negócio',
  'Custos',
  'Impressora',
  'Filamentos',
  'Canais',
  'Primeiro orçamento',
] as const;
export type StepState = 'on' | 'done' | 'skip' | 'todo' | 'locked';

/** Left-hand stepper (`.steps` in the design). */
export function Stepper({
  current,
  states,
  onPick,
}: {
  current: number;
  states: StepState[];
  onPick: (step: number) => void;
}) {
  return (
    <nav className="steps" aria-label="Passos da configuração">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const state = n === current ? 'on' : states[i];
        const cls =
          state === 'on'
            ? 'step on'
            : state === 'done'
              ? 'step done'
              : state === 'skip'
                ? 'step skip'
                : 'step';
        return (
          <button
            key={label}
            type="button"
            className={cls}
            style={{
              border: 0,
              background: state === 'on' ? undefined : 'transparent',
              cursor: state === 'locked' ? 'default' : 'pointer',
              font: 'inherit',
              textAlign: 'left',
            }}
            disabled={state === 'locked'}
            aria-current={state === 'on' ? 'step' : undefined}
            onClick={() => onPick(n)}
          >
            <span className="n">
              {state === 'done' ? <Icon name="check" size={14} strokeWidth={3} /> : n}
            </span>
            <span className="col" style={{ gap: 0 }}>
              <span>{label}</span>
              {state === 'skip' ? (
                <span className="xs muted">Pulado · completar depois</span>
              ) : null}
              {n === 1 && state !== 'skip' && state !== 'done' ? (
                <span className="xs muted">Opcional</span>
              ) : null}
            </span>
          </button>
        );
      })}
      <p className="xs muted" style={{ padding: '8px 12px' }}>
        Passos 2 a 5 são necessários para orçar. O 1 é opcional.
      </p>
    </nav>
  );
}

/** Wizard footer: Voltar · (Pular) · (Usar sugestões e continuar) · Continuar. */
export function WizardFooter({
  onBack,
  onSkip,
  onSuggest,
  onContinue,
  blockMsg,
  busy,
  continueLabel = 'Continuar',
}: {
  onBack?: () => void;
  onSkip?: () => void;
  onSuggest?: () => void;
  onContinue?: () => void;
  blockMsg?: string | null;
  busy?: boolean;
  continueLabel?: string;
}) {
  return (
    <div className="col" style={{ gap: 8 }}>
      {blockMsg ? (
        <p className="help err" role="status">
          <Icon name="alert" size={14} />
          {blockMsg}
        </p>
      ) : null}
      <div className="row wrap" style={{ gap: 10, justifyContent: 'space-between' }}>
        <div className="row" style={{ gap: 10 }}>
          {onBack ? (
            <button type="button" className="btn" onClick={onBack}>
              Voltar
            </button>
          ) : null}
          {onSkip ? (
            <button type="button" className="btn g" onClick={onSkip} disabled={busy}>
              Pular
            </button>
          ) : null}
        </div>
        <div className="row wrap" style={{ gap: 10 }}>
          {onSuggest ? (
            <button type="button" className="btn" onClick={onSuggest} disabled={busy}>
              Usar sugestões e continuar
            </button>
          ) : null}
          {onContinue ? (
            <button
              type="button"
              className="btn p"
              onClick={onContinue}
              disabled={busy || !!blockMsg}
            >
              {busy ? 'Salvando…' : continueLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function StepHead({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="col" style={{ gap: 6 }}>
      <h1 style={{ fontSize: 26 }}>{title}</h1>
      {children ? <p className="muted">{children}</p> : null}
    </div>
  );
}
